import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { EMAIL_PLATZHALTER, EMAIL_VERSAND_GRENZEN } from '@mcikm-vote/shared';

import {
  useEmailEinstellungen,
  useEmailEinstellungenSpeichern,
  useEmailTestversand,
} from '../../admin/emailEinstellungenApi';
import { Fehlermeldung } from '../../components/Fehlermeldung';
import { Feld } from '../../components/Feld';
import { Karte } from '../../components/Karte';
import { Knopf } from '../../components/Knopf';
import { RechtstextEditor } from '../../components/RechtstextEditor';

/**
 * E-Mail-Versand der Zugangskarten (ÄA-7).
 *
 * Bewusst **kein** Eingabefeld für ein SMTP-Passwort: Die Zugangsdaten des Postausgangsservers
 * kommen ausschließlich aus der `.env` (§5.1, §7.9), wie bei jedem anderen Geheimnis dieser
 * Anwendung. Diese Seite pflegt nur, was nicht geheim ist — Absendername, Absenderadresse, Betreff
 * und Mailtext — und zeigt, ob SMTP überhaupt eingerichtet ist.
 */
export function EmailEinstellungenSeite(): ReactElement {
  const abfrage = useEmailEinstellungen();
  const speichern = useEmailEinstellungenSpeichern();
  const testversand = useEmailTestversand();

  const [absenderName, setAbsenderName] = useState('');
  const [absenderAdresse, setAbsenderAdresse] = useState('');
  const [betreff, setBetreff] = useState('');
  const [mailtextMarkdown, setMailtextMarkdown] = useState('');
  const [gespeichert, setGespeichert] = useState(false);
  const [testadresse, setTestadresse] = useState('');
  const [testErfolg, setTestErfolg] = useState(false);

  const stand = abfrage.data;

  useEffect(() => {
    if (stand !== undefined) {
      setAbsenderName(stand.absenderName);
      setAbsenderAdresse(stand.absenderAdresse);
      setBetreff(stand.betreff);
      setMailtextMarkdown(stand.mailtextMarkdown);
    }
  }, [stand]);

  if (abfrage.isError) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">E-Mail-Versand</h1>
        <Fehlermeldung>{abfrage.error.message}</Fehlermeldung>
      </div>
    );
  }

  if (stand === undefined) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">E-Mail-Versand</h1>
        <p className="text-text-gedaempft text-sm">Wird geladen …</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold">E-Mail-Versand</h1>
      <p className="text-text-gedaempft text-sm">
        Für Online-Veranstaltungen: Zugangskarten lassen sich direkt per E-Mail an eine Liste von
        Adressen versenden — in der Tokenverwaltung der jeweiligen Veranstaltung. Diese Seite pflegt
        Absender und Mailtext.
      </p>

      <Karte>
        <h2 className="text-base font-semibold">Postausgangsserver</h2>
        {stand.smtpKonfiguriert ? (
          <p className="text-sm">
            <span className="text-erfolg font-medium">Konfiguriert.</span> Zugangsdaten kommen aus
            der Serverkonfiguration (<code>SMTP_HOST</code> u. a.) und werden hier nicht angezeigt.
          </p>
        ) : (
          <p className="text-text-gedaempft text-sm">
            <span className="text-warnung font-medium">Nicht konfiguriert.</span> Ohne{' '}
            <code>SMTP_HOST</code>, <code>SMTP_USER</code> und <code>SMTP_PASSWORD</code> in der{' '}
            <code>.env</code> gibt es keinen Versand — der Administrator ergänzt sie und startet den
            Container neu.
          </p>
        )}

        {stand.smtpKonfiguriert && (
          <form
            className="mt-3 flex flex-wrap items-end gap-2"
            onSubmit={(ereignis) => {
              ereignis.preventDefault();
              setTestErfolg(false);
              testversand.mutate(testadresse, { onSuccess: () => setTestErfolg(true) });
            }}
          >
            <div className="flex-1">
              <Feld
                name="testadresse"
                beschriftung="Test-E-Mail senden an"
                wert={testadresse}
                typ="email"
                pflicht
                onAendern={setTestadresse}
              />
            </div>
            <Knopf type="submit" art="sekundaer" disabled={testversand.isPending}>
              {testversand.isPending ? 'Wird gesendet …' : 'Senden'}
            </Knopf>
          </form>
        )}
        {testversand.isError && (
          <div className="mt-2">
            <Fehlermeldung>{testversand.error.message}</Fehlermeldung>
          </div>
        )}
        {testErfolg && !testversand.isPending && (
          <p role="status" className="mt-2 text-sm font-medium">
            Testmail versendet.
          </p>
        )}
      </Karte>

      <form
        className="flex flex-col gap-4"
        onSubmit={(ereignis) => {
          ereignis.preventDefault();
          setGespeichert(false);
          speichern.mutate(
            { absenderName, absenderAdresse, betreff, mailtextMarkdown },
            { onSuccess: () => setGespeichert(true) },
          );
        }}
      >
        <Karte>
          <h2 className="mb-3 text-base font-semibold">Absender</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Feld
              name="absenderName"
              beschriftung="Absendername"
              wert={absenderName}
              grenze={EMAIL_VERSAND_GRENZEN.absenderName}
              onAendern={setAbsenderName}
            />
            <Feld
              name="absenderAdresse"
              beschriftung="Absenderadresse"
              wert={absenderAdresse}
              typ="email"
              grenze={EMAIL_VERSAND_GRENZEN.absenderAdresse}
              hinweis="Muss zum Postausgangsserver passen, damit die Mails nicht abgewiesen werden."
              onAendern={setAbsenderAdresse}
            />
          </div>
        </Karte>

        <Karte>
          <h2 className="mb-3 text-base font-semibold">Mailtext</h2>
          <div className="mb-3">
            <Feld
              name="betreff"
              beschriftung="Betreff"
              wert={betreff}
              grenze={EMAIL_VERSAND_GRENZEN.betreff}
              onAendern={setBetreff}
            />
          </div>
          <RechtstextEditor
            name="mailtext"
            beschriftung="Text der E-Mail"
            wert={mailtextMarkdown}
            grenze={EMAIL_VERSAND_GRENZEN.mailtext}
            onAendern={setMailtextMarkdown}
          />
          <p className="text-text-gedaempft mt-2 text-xs">
            Platzhalter, die beim Versand je Empfänger ersetzt werden:{' '}
            {EMAIL_PLATZHALTER.map((platzhalter) => `{{${platzhalter}}}`).join(', ')}. Token und
            QR-Code stehen unabhängig davon immer im angehängten PDF.
          </p>
        </Karte>

        {speichern.isError && <Fehlermeldung>{speichern.error.message}</Fehlermeldung>}
        {gespeichert && !speichern.isPending && (
          <p role="status" className="text-sm font-medium">
            Gespeichert.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Knopf type="submit" disabled={speichern.isPending}>
            {speichern.isPending ? 'Wird gespeichert …' : 'Speichern'}
          </Knopf>
        </div>
      </form>
    </div>
  );
}
